import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

// Types
export interface FavoriteItem {
  id: string;
  name: string;
  url: string;
  addedDate: string;
  order: number;
}

export interface FavoritesData {
  [accountId: string]: FavoriteItem[];
}

interface FavoritesContextType {
  favorites: FavoriteItem[];
  canFavorite: boolean;
  isFavorited: boolean;
  addFavorite: (name: string, url?: string) => boolean;
  removeFavorite: (id: string) => void;
  reorderFavorites: (newOrder: FavoriteItem[]) => void;
  updateFavoriteName: (id: string, newName: string) => void;
  maxFavorites: number;
  canAddMore: boolean;
}

// Blacklisted routes that shouldn't be added to favorites
const BLACKLISTED_ROUTES = [
  '/', // Home page
  '/login',
  '/forgot',
  '/reset',
  '/invite',
  '/welcome',
  '/components',
  '/formbuilder',
  
  // MFA and authentication related screens
  /\/accounts\/settings\/.*\/mfa/,
  /\/accounts\/settings\/.*\/security/,
  
  // Dynamic edit/create screens with steps
  /\/create\/.*\/step/,
  /\/edit\/.*\/step/,
  /\/setup/,
  /\/clone\/.*\/step/,
  
  // Other sensitive areas
  /\/transfer\//,
  /\/payment/,
  /\/checkout/,
];

// Constants
const STORAGE_KEY = 'grassrootz_favorites';
const MAX_FAVORITES = 10;

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined);

interface FavoritesProviderProps {
  children: ReactNode;
  currentUserId: string;
}

export const FavoritesProvider: React.FC<FavoritesProviderProps> = ({
  children,
  currentUserId
}) => {
  const location = useLocation();
  const [favorites, setFavorites] = useState<FavoriteItem[]>([]);
  const [currentUrl, setCurrentUrl] = useState(window.location.pathname + window.location.search);

  // Update currentUrl when location changes or when URL changes manually
  useEffect(() => {
    const newUrl = window.location.pathname + window.location.search;
    setCurrentUrl(newUrl);
  }, [location]); // React to React Router location changes

  // Also listen for manual URL changes (like when filters change the URL without navigation)
  useEffect(() => {
    const handleUrlChange = () => {
      const newUrl = window.location.pathname + window.location.search;
      setCurrentUrl(newUrl);
    };

    // Listen for popstate events (back/forward button)
    window.addEventListener('popstate', handleUrlChange);
    
    // Also periodically check if URL changed (for cases where filters update URL without navigation)
    const urlCheckInterval = setInterval(handleUrlChange, 500);

    return () => {
      window.removeEventListener('popstate', handleUrlChange);
      clearInterval(urlCheckInterval);
    };
  }, []);

  // Load favorites from localStorage on mount or user change
  useEffect(() => {
    if (!currentUserId) return;
    
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const allFavorites: FavoritesData = JSON.parse(stored);
        const userFavorites = allFavorites[currentUserId] || [];
        // Sort by order
        userFavorites.sort((a, b) => a.order - b.order);
        setFavorites(userFavorites);
      } catch (error) {
        console.error('Error loading favorites:', error);
        setFavorites([]);
      }
    }
  }, [currentUserId]);  // Save favorites to localStorage
  const saveFavorites = (newFavorites: FavoriteItem[]) => {
    if (!currentUserId) return;

    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const allFavorites: FavoritesData = stored ? JSON.parse(stored) : {};
      allFavorites[currentUserId] = newFavorites;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(allFavorites));
      setFavorites(newFavorites);
    } catch (error) {
      console.error('Error saving favorites:', error);
    }
  };

  // Check if current page can be favorited
  const canFavorite = (url: string = window.location.pathname + window.location.search) => {
    // For blacklist checking, we only check the pathname (not query params)
    const pathOnly = url.split('?')[0];
    return !BLACKLISTED_ROUTES.some(route => {
      if (typeof route === 'string') {
        return pathOnly === route;
      } else {
        return route.test(pathOnly);
      }
    });
  };

  // Check if current page is already favorited
  const isFavorited = (url: string = window.location.pathname + window.location.search) => {
    return favorites.some(fav => fav.url === url);
  };  // Add favorite
  const addFavorite = (name: string, url: string = window.location.pathname + window.location.search) => {
    if (!canFavorite(url) || isFavorited(url) || favorites.length >= MAX_FAVORITES) {
      return false;
    }

    const newFavorite: FavoriteItem = {
      id: `fav_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: name.trim(),
      url,
      addedDate: new Date().toISOString(),
      order: favorites.length
    };

    const newFavorites = [...favorites, newFavorite];
    saveFavorites(newFavorites);
    return true;
  };

  // Remove favorite
  const removeFavorite = (id: string) => {
    const newFavorites = favorites
      .filter(fav => fav.id !== id)
      .map((fav, index) => ({ ...fav, order: index })); // Reorder
    saveFavorites(newFavorites);
  };

  // Reorder favorites
  const reorderFavorites = (newOrder: FavoriteItem[]) => {
    const reorderedFavorites = newOrder.map((fav, index) => ({
      ...fav,
      order: index
    }));
    saveFavorites(reorderedFavorites);
  };

  // Update favorite name
  const updateFavoriteName = (id: string, newName: string) => {
    const newFavorites = favorites.map(fav =>
      fav.id === id ? { ...fav, name: newName.trim() } : fav
    );
    saveFavorites(newFavorites);
  };

  const contextValue: FavoritesContextType = {
    favorites,
    canFavorite: canFavorite(currentUrl),
    isFavorited: isFavorited(currentUrl),
    addFavorite,
    removeFavorite,
    reorderFavorites,
    updateFavoriteName,
    maxFavorites: MAX_FAVORITES,
    canAddMore: favorites.length < MAX_FAVORITES
  };

  return (
    <FavoritesContext.Provider value={contextValue}>
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = (): FavoritesContextType => {
  const context = useContext(FavoritesContext);
  if (context === undefined) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};
